import React, { useState, useEffect, useCallback, useMemo } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';

/**
 * Which suppliers are international and/or restricted.
 *
 * Two independent flags, because they answer different questions:
 *  - international only changes how a sheet reads (cover in months, and the
 *    "International only" filter), and
 *  - restricted actually hides those items from anyone without the
 *    "Restricted suppliers" tick on the Branch Access screen.
 *
 * They started life as one hardcoded Python list, which is why every seeded
 * supplier has both set.
 */
const SupplierFlagsCard = ({ onToast }) => {
    const [flags, setFlags] = useState([]);
    const [knownNames, setKnownNames] = useState([]);
    const [name, setName] = useState('');
    const [isInternational, setIsInternational] = useState(true);
    const [isRestricted, setIsRestricted] = useState(true);
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const [flagData, nameData] = await Promise.all([
                stockReorderApi.listSupplierFlags(),
                stockReorderApi.listKnownSupplierNames()
            ]);
            setFlags(flagData.supplier_flags || []);
            setKnownNames(nameData.names || []);
        } catch (err) {
            setError(errorMessage(err, 'Could not load the supplier list'));
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    /** Names from the supplier master that aren't flagged yet. */
    const suggestions = useMemo(() => {
        const taken = new Set(flags.map((f) => f.name.trim().toUpperCase()));
        return knownNames.filter((n) => !taken.has(n.trim().toUpperCase()));
    }, [knownNames, flags]);

    const add = async () => {
        if (!name.trim()) return;
        setBusy(true);
        setError('');
        try {
            await stockReorderApi.addSupplierFlag({ name: name.trim(), isInternational, isRestricted });
            onToast?.(`“${name.trim()}” added.`);
            setName('');
            await load();
        } catch (err) {
            setError(errorMessage(err, 'Could not add that supplier'));
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (flag, which) => {
        const next = {
            isInternational: which === 'international' ? !flag.is_international : flag.is_international,
            isRestricted: which === 'restricted' ? !flag.is_restricted : flag.is_restricted
        };
        // A row with neither flag does nothing; the server rejects it, so say so
        // here rather than letting the checkbox bounce back with an error.
        if (!next.isInternational && !next.isRestricted) {
            setError(
                `“${flag.name}” would have neither flag set — remove it from the list instead.`
            );
            return;
        }
        setSavingId(flag.id);
        setError('');
        try {
            const updated = await stockReorderApi.updateSupplierFlag(flag.id, next);
            setFlags((list) => list.map((f) => (f.id === flag.id ? updated : f)));
        } catch (err) {
            setError(errorMessage(err, 'Could not save that change'));
            await load();
        } finally {
            setSavingId(null);
        }
    };

    const remove = async (flag) => {
        if (
            !window.confirm(
                `Remove “${flag.name}”? Items from this supplier will become visible to everyone with access to the branch.`
            )
        )
            return;
        setSavingId(flag.id);
        setError('');
        try {
            await stockReorderApi.deleteSupplierFlag(flag.id);
            onToast?.(`“${flag.name}” removed from the list.`);
            await load();
        } catch (err) {
            setError(errorMessage(err, 'Could not remove that supplier'));
        } finally {
            setSavingId(null);
        }
    };

    const term = search.trim().toLowerCase();
    const visible = term ? flags.filter((f) => f.name.toLowerCase().includes(term)) : flags;

    return (
        <section className="sr-card">
            <h2 className="sr-card-title">International &amp; restricted suppliers</h2>
            <p className="sr-hint">
                <strong>International</strong> changes how a sheet reads — cover in months instead of
                days, and the “International only” filter. <strong>Restricted</strong> hides those
                items entirely from anyone without <em>Restricted suppliers</em> ticked on Branch
                Access. Matching is on the supplier name, ignoring case and extra spaces.
            </p>

            <div className="sr-inline-form">
                <input
                    type="text"
                    list="sr-known-suppliers"
                    placeholder="Supplier name — pick a known one or type it"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') add();
                    }}
                />
                <datalist id="sr-known-suppliers">
                    {suggestions.map((n) => (
                        <option key={n} value={n} />
                    ))}
                </datalist>
                <label className="sr-check">
                    <input
                        type="checkbox"
                        checked={isInternational}
                        onChange={(e) => setIsInternational(e.target.checked)}
                    />
                    International
                </label>
                <label className="sr-check">
                    <input
                        type="checkbox"
                        checked={isRestricted}
                        onChange={(e) => setIsRestricted(e.target.checked)}
                    />
                    Restricted
                </label>
                <button
                    className="sr-btn sr-btn-primary"
                    onClick={add}
                    disabled={busy || !name.trim() || (!isInternational && !isRestricted)}
                >
                    Add supplier
                </button>
            </div>
            <p className="sr-hint">
                {suggestions.length > 0
                    ? `${suggestions.length} supplier name(s) from the supplier master are not on this list yet — start typing to pick one.`
                    : 'Upload a supplier mapping file above and its supplier names become selectable here.'}
            </p>

            {error && <div className="sr-alert sr-alert-error">{error}</div>}

            <div className="sr-filters">
                <input
                    type="search"
                    placeholder="Search this list…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <span className="sr-muted">
                    {visible.length} of {flags.length}
                </span>
            </div>

            <div className="sr-table-wrap">
                <table className="sr-table">
                    <thead>
                        <tr>
                            <th>Supplier</th>
                            <th>International</th>
                            <th>Restricted</th>
                            <th>Added by</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((flag) => (
                            <tr key={flag.id}>
                                <td>{flag.name}</td>
                                <td>
                                    <label className="sr-check">
                                        <input
                                            type="checkbox"
                                            checked={flag.is_international}
                                            disabled={savingId === flag.id}
                                            onChange={() => toggle(flag, 'international')}
                                        />
                                    </label>
                                </td>
                                <td>
                                    <label className="sr-check">
                                        <input
                                            type="checkbox"
                                            checked={flag.is_restricted}
                                            disabled={savingId === flag.id}
                                            onChange={() => toggle(flag, 'restricted')}
                                        />
                                    </label>
                                </td>
                                <td className="sr-muted">
                                    {flag.created_by === 'seed:migration' ? 'original list' : 'admin'}
                                </td>
                                <td className="sr-right">
                                    <button
                                        className="sr-btn sr-btn-sm sr-btn-danger"
                                        onClick={() => remove(flag)}
                                        disabled={savingId === flag.id}
                                    >
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={5} className="sr-empty">
                                    {busy ? 'Loading…' : 'No suppliers flagged.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default SupplierFlagsCard;
